/**
 * User filter: user identity properties, event count, transaction count.
 * Used by UserPropertyFilterModule on the Users list page.
 * Field names must match userIdentityFilterBuilder fieldMap.
 */

// Fixed user properties - field names match backend userIdentityFilterBuilder
export const getFixedUserProperties = () => [
  { name: 'userId', label: 'User ID', type: 'string', source: 'user' },
  { name: 'isIdentified', label: 'Is Identified', type: 'boolean', source: 'user' },
  { name: 'hasWalletAddress', label: 'Has Wallet Address', type: 'boolean', source: 'user' },
  { name: 'isBounceUser', label: 'Is Bounce', type: 'boolean', source: 'user' },
  { name: 'isWeb3User', label: 'Is Web3 User', type: 'boolean', source: 'user' },
  { name: 'firstVisit', label: 'First Visit', type: 'datetime', source: 'user' },
  { name: 'lastVisit', label: 'Last Visit', type: 'datetime', source: 'user' },
  { name: 'totalPageViews', label: 'Total Page Views', type: 'number', source: 'user' },
  { name: 'totalTimeSpent', label: 'Total Time Spent', type: 'number', source: 'user' },
  { name: 'totalSessions', label: 'Total Sessions', type: 'number', source: 'user' },
  { name: 'firstReferrer', label: 'First Referrer', type: 'string', source: 'user' },
  { name: 'lastReferrer', label: 'Last Referrer', type: 'string', source: 'user' },
  { name: 'firstCity', label: 'First Location City', type: 'string', source: 'user' },
  { name: 'firstCountry', label: 'First Location Country', type: 'string', source: 'user' },
  { name: 'lastCity', label: 'Last Location City', type: 'string', source: 'user' },
  { name: 'lastCountry', label: 'Last Location Country', type: 'string', source: 'user' },
  { name: 'deviceType', label: 'Device Type', type: 'string', source: 'user' },
  { name: 'browser', label: 'Browser Name', type: 'string', source: 'user' },
  { name: 'operatingSystem', label: 'Operating System', type: 'string', source: 'user' },
  { name: 'walletType', label: 'Wallet Type', type: 'string', source: 'user' },
  { name: 'walletChain', label: 'Wallet Chain', type: 'string', source: 'user' },
  { name: 'hasCustomProperties', label: 'Has Custom Properties', type: 'boolean', source: 'user' },
];

// Did/Did not operators for WHO block (Events and Transactions)
export const getDidOperators = () => [
  { value: 'did', label: 'Did' },
  { value: 'did_not', label: 'Did not' },
];

// Measure types: what we're measuring (Total Events/Transactions vs Aggregate property)
export const getMeasureTypeLabel = (source, measureType) => {
  if (measureType === 'aggregate_property') return 'Aggregate property';
  return source === 'event' ? 'Total Events' : 'Total Transactions';
};

// Aggregate sub-options (shown when "Aggregate property" is hovered)
export const getAggregateTypes = () => [
  { value: 'sum', label: 'Sum' },
  { value: 'average', label: 'Average' },
  { value: 'distinct_count', label: 'Distinct Count' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
];

// Fixed numeric event properties (for aggregate property selector)
export const getNumericEventProperties = () => [
  { name: 'viewport_height', label: 'Viewport Height', type: 'number', source: 'event' },
  { name: 'viewport_width', label: 'Viewport Width', type: 'number', source: 'event' },
  { name: 'screen_height', label: 'Screen Height', type: 'number', source: 'event' },
  { name: 'screen_width', label: 'Screen Width', type: 'number', source: 'event' },
  { name: 'dpi', label: 'DPI', type: 'number', source: 'event' },
];

// Fixed numeric transaction properties
export const getNumericTransactionProperties = () => [
  { name: 'block_no', label: 'Block no', type: 'number', source: 'transaction' },
  { name: 'value', label: 'Value', type: 'number', source: 'transaction' },
  { name: 'decoded_input.uint256', label: 'Decoded: uint256', type: 'number', source: 'transaction' },
];

// Numeric operators for event_count and transaction_count
export const getNumericCountOperators = () => [
  { value: 'equal', label: 'Equals' },
  { value: 'not_equal', label: 'Not equal' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'greater_than_equal', label: 'Greater than or equal to' },
  { value: 'less_than', label: 'Less than' },
  { value: 'less_than_equal', label: 'Less than or equal to' },
  { value: 'between', label: 'Between' },
  { value: 'not_between', label: 'Not between' },
  { value: 'is_numeric', label: 'Is numeric' },
  { value: 'is_not_numeric', label: 'Is not numeric' },
];

function getDefaultOperator(type) {
  switch (type) {
    case 'string': return 'is';
    case 'number': return 'equal';
    case 'boolean': return 'is_true';
    case 'list': return 'is';
    case 'object': return 'is';
    case 'datetime':
    case 'date': return 'between';
    default: return 'is';
  }
}

// Operators for user properties by type (same as transactionFilterProperties)
export const getOperatorsForUserType = (type) => {
  switch (type) {
    case 'string':
      return [
        { value: 'is', label: 'Is' },
        { value: 'is_not', label: 'Is not' },
        { value: 'contains', label: 'Contains' },
        { value: 'does_not_contain', label: 'Does not contain' },
        { value: 'is_set', label: 'Is set' },
        { value: 'is_not_set', label: 'Is not set' },
      ];
    case 'number':
      return getNumericCountOperators();
    case 'boolean':
      return [
        { value: 'is_true', label: 'Is true' },
        { value: 'is_false', label: 'Is false' },
      ];
    case 'list':
      return [
        { value: 'is', label: 'Is' },
        { value: 'is_not', label: 'Is not' },
        { value: 'contains', label: 'Contains' },
        { value: 'does_not_contain', label: 'Does not contain' },
        { value: 'is_set', label: 'Is set' },
        { value: 'is_not_set', label: 'Is not set' },
      ];
    case 'object':
      return [
        { value: 'is', label: 'Is' },
        { value: 'is_not', label: 'Is not' },
        { value: 'contains', label: 'Contains' },
        { value: 'does_not_contain', label: 'Does not contain' },
        { value: 'is_set', label: 'Is set' },
        { value: 'is_not_set', label: 'Is not set' },
      ];
    case 'datetime':
    case 'date':
      return [
        { value: 'last', label: 'Last' },
        { value: 'not_in_the_last', label: 'Not in the last' },
        { value: 'between', label: 'Between' },
        { value: 'not_between', label: 'Not between' },
        { value: 'on', label: 'On' },
        { value: 'not_on', label: 'Not on' },
        { value: 'before_the_last', label: 'Before the last' },
        { value: 'before', label: 'Before' },
        { value: 'since', label: 'Since' },
        { value: 'in_the_next', label: 'In the next' },
      ];
    default:
      return [
        { value: 'is', label: 'Is' },
        { value: 'is_not', label: 'Is not' },
        { value: 'contains', label: 'Contains' },
        { value: 'is_set', label: 'Is set' },
        { value: 'is_not_set', label: 'Is not set' },
      ];
  }
};

export { getDefaultOperator };
